import { useState, useEffect, useRef, useCallback } from "react";
import { getNextQuestion } from "./Paragraphmodule";
import { generateValidatedParagraph } from "./Paragraphmodule";

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function fmt(s) {
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

body {
  background: #fff;
  color: #111;
  font-family: 'Libre Baskerville', Georgia, serif;
  -webkit-font-smoothing: antialiased;
}

.pj {
  min-height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
}

/* ── Splash ── */
.pj-splash {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 24px;
}

.pj-splash-title {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #111;
}

.pj-splash-sub {
  font-size: 0.9rem;
  color: #888;
  font-style: italic;
  margin-bottom: 28px;
  text-align: center;
  max-width: 340px;
  line-height: 1.6;
}

.pj-start-btn {
  font-family: 'Libre Baskerville', Georgia, serif;
  font-size: 0.9rem;
  padding: 11px 36px;
  border: 1.5px solid #111;
  background: #111;
  color: #fff;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: background 0.15s, color 0.15s;
}

.pj-start-btn:hover {
  background: #fff;
  color: #111;
}

/* ── Top bar ── */
.pj-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  border-bottom: 1px solid #e8e8e8;
}

.pj-bar-title {
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #aaa;
  font-family: 'DM Mono', monospace;
}

.pj-timer {
  font-family: 'DM Mono', monospace;
  font-size: 1.1rem;
  font-weight: 500;
  color: #111;
  letter-spacing: 0.06em;
  transition: color 0.3s;
}

.pj-timer.hot { color: #c0392b; }

/* ── Main ── */
.pj-main {
  flex: 1;
  max-width: 680px;
  width: 100%;
  margin: 0 auto;
  padding: 36px 28px 60px;
}

/* ── Loading ── */
.pj-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 80px 0;
}

.pj-loading-text {
  font-size: 0.78rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #bbb;
  font-family: 'DM Mono', monospace;
}

.pj-dots { display: flex; gap: 6px; }

.pj-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #ccc;
  animation: bdot 1.1s ease infinite;
}
.pj-dot:nth-child(2) { animation-delay: 0.18s; }
.pj-dot:nth-child(3) { animation-delay: 0.36s; }

@keyframes bdot {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
  40% { transform: translateY(-8px); opacity: 1; }
}

/* ── Sentences ── */
.pj-sentences {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 32px;
  border-top: 1px solid #eee;
}

.pj-row {
  display: flex;
  gap: 0;
  align-items: baseline;
  border-bottom: 1px solid #eee;
  padding: 14px 0;
  animation: fadein 0.3s ease both;
}

.pj-row:nth-child(1) { animation-delay: 0.04s; }
.pj-row:nth-child(2) { animation-delay: 0.08s; }
.pj-row:nth-child(3) { animation-delay: 0.12s; }
.pj-row:nth-child(4) { animation-delay: 0.16s; }
.pj-row:nth-child(5) { animation-delay: 0.20s; }

@keyframes fadein {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.pj-row-num {
  font-family: 'DM Mono', monospace;
  font-size: 0.72rem;
  color: #ccc;
  width: 28px;
  flex-shrink: 0;
  padding-top: 2px;
}

.pj-row-text {
  font-size: 0.94rem;
  line-height: 1.65;
  color: #222;
  font-weight: 400;
}

/* ── Input area ── */
.pj-input-area {
  margin-bottom: 20px;
}

.pj-input-label {
  font-family: 'DM Mono', monospace;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #bbb;
  margin-bottom: 8px;
}

.pj-input {
  font-family: 'DM Mono', monospace;
  font-size: 1.8rem;
  letter-spacing: 0.35em;
  border: none;
  border-bottom: 1.5px solid #ddd;
  background: transparent;
  color: #111;
  padding: 4px 0;
  outline: none;
  width: 180px;
  transition: border-color 0.2s;
}

.pj-input:focus { border-bottom-color: #111; }
.pj-input.ok   { border-bottom-color: #27ae60; color: #27ae60; }
.pj-input.bad  { border-bottom-color: #c0392b; color: #c0392b; }

.pj-input-hint {
  font-size: 0.74rem;
  color: #ccc;
  margin-top: 6px;
  font-style: italic;
}

/* ── Feedback ── */
.pj-feedback {
  font-size: 0.82rem;
  font-family: 'DM Mono', monospace;
  letter-spacing: 0.04em;
  margin-bottom: 20px;
  padding: 10px 14px;
  border-left: 3px solid;
  animation: fadein 0.2s ease both;
}

.pj-feedback.ok  { border-color: #27ae60; color: #27ae60; background: #f6fdf9; }
.pj-feedback.bad { border-color: #c0392b; color: #c0392b; background: #fdf6f6; }

/* ── Buttons ── */
.pj-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.btn {
  font-family: 'Libre Baskerville', serif;
  font-size: 0.82rem;
  padding: 8px 20px;
  border: 1.5px solid #111;
  background: #111;
  color: #fff;
  cursor: pointer;
  letter-spacing: 0.02em;
  transition: background 0.15s, color 0.15s;
}

.btn:hover { background: #fff; color: #111; }

.btn.ghost {
  background: #fff;
  color: #888;
  border-color: #ddd;
}

.btn.ghost:hover { border-color: #888; color: #111; }

/* ── History ── */
.pj-history {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid #eee;
}

.pj-history-head {
  font-family: 'DM Mono', monospace;
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #bbb;
  margin-bottom: 16px;
}

.pj-history-list { display: flex; flex-direction: column; gap: 1px; border-top: 1px solid #eee; }

.pj-h-row {
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  font-size: 0.82rem;
}

.pj-h-badge {
  font-family: 'DM Mono', monospace;
  font-size: 0.66rem;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border: 1px solid;
  white-space: nowrap;
  flex-shrink: 0;
}

.pj-h-badge.ok      { color: #27ae60; border-color: #27ae60; }
.pj-h-badge.bad     { color: #c0392b; border-color: #c0392b; }
.pj-h-badge.skipped { color: #aaa;    border-color: #ddd; }

.pj-h-preview {
  flex: 1;
  color: #aaa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
  font-size: 0.8rem;
}

.pj-h-time {
  font-family: 'DM Mono', monospace;
  font-size: 0.7rem;
  color: #ccc;
  white-space: nowrap;
}
`;

// ─── Component ────────────────────────────────────────────────────────────────
export default function ParaJumble() {
    const [phase, setPhase] = useState("splash"); // splash | loading | playing
    const [question, setQuestion] = useState(null);
    const [answer, setAnswer] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [feedback, setFeedback] = useState(null); // null | 'ok' | 'bad'
    const [correctOrder, setCorrectOrder] = useState("");
    const [time, setTime] = useState(0);
    const [history, setHistory] = useState([]);
    const [inputText, setInputText] = useState("");
    const [loading, setLoading] = useState(false);
    const [blurInput, setBlurInput] = useState(false);
    const [started, setStarted] = useState(false);

    const timerRef = useRef(null);

    const startTimer = () => {
        clearInterval(timerRef.current);
        setTime(0);
        timerRef.current = setInterval(() => setTime((t) => t + 1), 1000);
    };

    const stopTimer = () => clearInterval(timerRef.current);

    useEffect(() => () => clearInterval(timerRef.current), []);

    // ── Load next ──────────────────────────────────────────────────────────────
    const loadQuestion = useCallback(async () => {
        if (!inputText.trim()) return;

        stopTimer();

        setLoading(true);
        setAnswer("");
        setSubmitted(false);
        setFeedback(null);
        setCorrectOrder("");
        setQuestion(null);
        setStarted(false);

        const res = await generateValidatedParagraph(
             inputText
        );

        setLoading(false);

        if (!res.success) {
            alert(res.error);
            return;
        }

        const win = res.result;

        const keys = shuffle([1, 2, 3, 4, 5]);

        setQuestion({
            window: win,
            displayOrder: keys,
        });

        setBlurInput(true);
    }, [inputText]);

    // ── Start button ──────────────────────────────────────────────────────────
    // const handleStart = () => loadNext();

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!question) return;

        // First click → start timer
        if (!started) {
            setStarted(true);
            startTimer();
            return;
        }

        if (submitted) return;

        const digits = answer.trim().split("").map(Number);

        if (
            digits.length !== 5 ||
            digits.some((d) => d < 1 || d > 5 || isNaN(d))
        ) {
            return;
        }

        stopTimer();

        const isCorrect = digits.every(
            (d, i) => question.displayOrder[d - 1] === i + 1
        );

        const correct = [1, 2, 3, 4, 5]
            .map((wk) => question.displayOrder.indexOf(wk) + 1)
            .join("");

        setFeedback(isCorrect ? "ok" : "bad");
        setCorrectOrder(correct);
        setSubmitted(true);

        setHistory((prev) => [
            {
                id: Date.now(),
                result: isCorrect ? "ok" : "bad",
                preview: question.window[1],
                timeTaken: time,
                answer: answer.trim(),
            },
            ...prev,
        ]);
    };
    // ── Skip ───────────────────────────────────────────────────────────────────

    return (
        <>
            <style>{css}</style>

            <div
                style={{
                    display: "flex",
                    height: "100vh",
                    background: "#fff",
                }}
            >

                {/* LEFT SIDE */}
                <div
                    style={{
                        width: "50%",
                        borderRight: "1px solid #eee",
                        padding: "40px",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >

                    {/* Blur Overlay */}
                    {blurInput && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                backdropFilter: "blur(10px)",
                                background: "rgba(255,255,255,0.4)",
                                zIndex: 2,
                            }}
                        />
                    )}

                    <div
                        style={{
                            position: "relative",
                            zIndex: 3,
                        }}
                    >
                        <div
                            style={{
                                fontSize: "1.5rem",
                                marginBottom: "24px",
                                fontWeight: 700,
                            }}
                        >
                            Input Text
                        </div>

                        <textarea
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Enter your paragraph topic or source text..."
                            style={{
                                width: "100%",
                                height: "300px",
                                padding: "18px",
                                fontSize: "1rem",
                                lineHeight: 1.7,
                                border: "1px solid #ddd",
                                outline: "none",
                                resize: "none",
                                fontFamily: "Libre Baskerville",
                                background: "#fff",
                            }}
                        />

                        <div
                            style={{
                                display: "flex",
                                gap: "12px",
                                marginTop: "20px",
                            }}
                        >
                            <button className="btn" onClick={loadQuestion}>
                                Generate Question
                            </button>

                            {blurInput && (
                                <button
                                    className="btn ghost"
                                    onClick={() => setBlurInput((v) => !v)}
                                >
                                    {blurInput ? "Unblur" : "Blur"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT SIDE */}
                <div
                    style={{
                        width: "50%",
                        padding: "40px",
                        overflowY: "auto",
                    }}
                >

                    {!question && !loading && (
                        <div
                            style={{
                                color: "#bbb",
                                fontStyle: "italic",
                                marginTop: "80px",
                                textAlign: "center",
                            }}
                        >
                            Generated question will appear here
                        </div>
                    )}

                    {loading && (
                        <div className="pj-loading">
                            <div className="pj-dots">
                                <div className="pj-dot" />
                                <div className="pj-dot" />
                                <div className="pj-dot" />
                            </div>

                            <div className="pj-loading-text">
                                Generating paragraph
                            </div>
                        </div>
                    )}

                    {question && (
                        <>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginBottom: "30px",
                                    alignItems: "center",
                                }}
                            >
                                <div className="pj-bar-title">
                                    Arrange in correct order
                                </div>

                                <div className={`pj-timer${time > 60 ? " hot" : ""}`}>
                                    {fmt(time)}
                                </div>
                            </div>

                            <div className="pj-sentences">
                                {question.displayOrder.map((wKey, i) => (
                                    <div className="pj-row" key={i}>
                                        <span className="pj-row-num">
                                            {i + 1}
                                        </span>

                                        <span className="pj-row-text">
                                            {question.window[wKey]}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="pj-input-area">
                                <div className="pj-input-label">
                                    Correct order
                                </div>

                                <input
                                    className={`pj-input${feedback
                                            ? feedback === "ok"
                                                ? " ok"
                                                : " bad"
                                            : ""
                                        }`}
                                    type="text"
                                    maxLength={5}
                                    value={answer}
                                    disabled={!started || submitted}
                                    placeholder="12345"
                                    onChange={(e) =>
                                        setAnswer(
                                            e.target.value.replace(/[^1-5]/g, "")
                                        )
                                    }
                                />
                            </div>

                            {feedback && (
                                <div className={`pj-feedback ${feedback}`}>
                                    {feedback === "ok"
                                        ? "Correct"
                                        : `Wrong — correct order: ${correctOrder}`}
                                </div>
                            )}

                            <div className="pj-actions">

                                {!started && (
                                    <button className="btn" onClick={handleSubmit}>
                                        Start Timer
                                    </button>
                                )}

                                {started && !submitted && (
                                    <button className="btn" onClick={handleSubmit}>
                                        Submit Answer
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}